import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { StyledFormLabel } from '../../forms/FormLabel';
import { LyraFormInputText } from '../../adGenerator/InputText';
import { FlexBoxColumn } from '../../FlexBoxColumn';
import { KeyboardArrowRightOutlined } from '@mui/icons-material';
import { FlexBoxRow } from '../../FlexBoxRow';
import { FormStepsNextButton } from '../../styled/FormStepsNextButton';
import { useWebsiteGraderContext, WebsiteGraderStep } from 'src/components/providers/websiteGrader';
import { useSubmitBusinessDetailsForm } from './useSubmitBusinessDetailsForm';
import {alpha, Typography, useMediaQuery} from '@mui/material';
import { LyraTheme as theme } from 'src/components/theme/lyra/Theme';
import { WebSiteGraderStepper } from '../Stepper';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { LyraFormInputSelect } from '../../adGenerator/Select';
import { BusinessType } from 'src/enums/BusinessType';
import { useSearchParams } from 'react-router-dom';
import { urlRegex } from 'src/components/facebook/formStepTwo/FormStepTwo';
import { FontWeight } from 'src/components/theme/lyra/FontWeight';

import * as Yup from 'yup';

const getValidationSchema = ({ t }: { t: TFunction }) =>
  Yup.object().shape({
    businessName: Yup.string().required(t('errors.required', { ns: 'common' })),
    website: Yup.string()
      .required(t('errors.required', { ns: 'common' }))
      .matches(urlRegex, t('errors.websiteNotValid', { ns: 'common' })),
    businessType: Yup.string().required(t('errors.required', { ns: 'common' })),
  });

export const WebsiteGraderBusinessDetailsForm = () => {
  const { t } = useTranslation(['websiteGrader', 'common']);
  const [queryParams] = useSearchParams();
  const context = useWebsiteGraderContext();
  const methods = useForm({
    defaultValues: {
      businessName: context.businessDetails.businessName,
      website: context.businessDetails.website ? context.businessDetails.website : queryParams.get('website') ?? '',
      businessType: context.businessDetails.businessType,
    },
    mode: 'onChange',
    resolver: yupResolver(getValidationSchema({ t })),
  });
  const { disabled, onSubmit } = useSubmitBusinessDetailsForm(methods.getValues);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <FormProvider {...methods}>
      <WebSiteGraderStepper currentStep={WebsiteGraderStep.COLLECT_BUSINESS_DETAILS} />
      <FlexBoxColumn sx={{ alignSelf: 'stretch', paddingY: isMobile ? 3 : 5 }}>
        <Typography variant="h3" textAlign={'center'}>
          {t('form_collect_business_details.heading')}
        </Typography>
      </FlexBoxColumn>
      <FlexBoxColumn sx={{ justifyContent: 'flex-start', width: '100%', gap: 3 }}>
        <FlexBoxColumn sx={{ justifyContent: 'flex-start', width: '100%', gap: 1 }}>
          <StyledFormLabel sx={{ fontWeight: FontWeight.SemiBold, color: 'grey.900' }}>
            {t('form_collect_business_details.businessName')}
          </StyledFormLabel>
          <LyraFormInputText
            name="businessName"
            size="small"
            placeholder={t('form_collect_business_details.businessName')}
          />
        </FlexBoxColumn>
        <FlexBoxColumn sx={{ justifyContent: 'flex-start', width: '100%', gap: 1 }}>
          <StyledFormLabel sx={{ fontWeight: FontWeight.SemiBold, color: 'grey.900' }}>
            {t('form_collect_business_details.website')}
          </StyledFormLabel>
          <LyraFormInputText name="website" size="small" placeholder={t('form_collect_business_details.website')} />
        </FlexBoxColumn>
        <FlexBoxColumn sx={{ justifyContent: 'flex-start', width: '100%', gap: 1 }}>
          <StyledFormLabel sx={{ fontWeight: FontWeight.SemiBold, color: 'grey.900' }}>
            {t('form_collect_business_details.businessType')}
          </StyledFormLabel>
          <LyraFormInputSelect
            testid="businessTypeSelect"
            name="businessType"
            options={Object.values(BusinessType).map((businessType) => ({ name: businessType, id: businessType }))}
            size="small"
          />
        </FlexBoxColumn>
      </FlexBoxColumn>
      <FlexBoxRow sx={{ justifyContent: 'center', width: '100%', mt: isMobile ? 3 : 5 }}>
        <FormStepsNextButton
          disabled={disabled}
          onSubmit={onSubmit}
          id="website-grader-collect-business-details-next"
          color="primary"
          endIcon={<KeyboardArrowRightOutlined />}
          sx={{
            borderRadius: '12px',
            height: 40,
            padding: `${theme.spacing(1.25)} ${theme.spacing(2.5)}`,
            textTransform: 'initial',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.88),
            },
            '&:active': {
              backgroundColor: alpha(theme.palette.primary.main, 0.80),
            },
          }}
        />
      </FlexBoxRow>
    </FormProvider>
  );
};
